import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { DsAlert, DsButton } from '@/modules/internal-v2/ds';
import { useImportCyberport } from '@/api/specifications/specifications.hooks';
import type {
  IdentifiedProductLine,
  IdentifiedProductModel,
  IdentifiedVocabulary,
  ImportCyberportMode,
  ImportCyberportPolicy,
  ImportCyberportResult,
} from '@/api/specifications/specifications.types';

type Format = 'cyberport';

interface ParsedFile {
  name: string;
  bytes: number;
  hits: unknown[];
}

/**
 * Imports tab: upload a scraper JSON dump and route it through the
 * specifications submodule's importer. Cyberport is the only format today;
 * the format dropdown leaves space for future scrapers without restructuring.
 */
export function ImportsSection({ canManage }: { canManage: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const importMut = useImportCyberport();

  const [format, setFormat] = useState<Format>('cyberport');
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mode, setMode] = useState<ImportCyberportMode>('both');
  const [policy, setPolicy] = useState<ImportCyberportPolicy>('fill-if-empty');
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<ImportCyberportResult | null>(null);

  async function handleFile(f: File | null): Promise<void> {
    setFile(null);
    setParseError(null);
    setResult(null);
    if (!f) return;
    try {
      const text = await f.text();
      const parsed: unknown = JSON.parse(text);
      const hits = Array.isArray(parsed) ? parsed : null;
      if (!hits) {
        throw new Error('Expected a top-level JSON array of hits');
      }
      setFile({ name: f.name, bytes: f.size, hits });
    } catch (e) {
      setParseError(
        e instanceof Error ? e.message : 'Could not parse file as JSON',
      );
    }
  }

  function runImport(): void {
    if (!file) return;
    importMut.mutate(
      { mode, policy, dryRun, hits: file.hits },
      {
        onSuccess: (res) => {
          setResult(res);
          if (dryRun) {
            enqueueSnackbar('Dry-Run abgeschlossen (keine Schreibvorgänge)', {
              variant: 'info',
            });
          } else {
            enqueueSnackbar(
              `Import abgeschlossen: ${res.totals.itemsUpdated} Artikel aktualisiert`,
              { variant: 'success' },
            );
          }
        },
        onError: (e) =>
          enqueueSnackbar(`Import fehlgeschlagen: ${(e as Error).message}`, {
            variant: 'error',
          }),
      },
    );
  }

  return (
    <div className="sp-imports">
      <div className="sp-imports-card">
        <h3 className="sp-imports-title">Scraper-Dump importieren</h3>
        <p className="sp-imports-help">
          Verknüpft Cyberport-Treffer per GTIN / Herstellernummer mit unseren
          Artikeln und legt vorgeschlagene productLines, Modelle und
          Farbvokabular für das Review an. Bilder werden nicht importiert.
        </p>

        <div className="sp-imports-row">
          <label className="sp-imports-field">
            <span className="sp-imports-label">Format</span>
            <select
              className="sp-imports-select"
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
              disabled
            >
              <option value="cyberport">Cyberport (JSON)</option>
            </select>
          </label>

          <label className="sp-imports-field sp-imports-field-grow">
            <span className="sp-imports-label">Datei</span>
            <input
              type="file"
              accept="application/json,.json"
              className="sp-imports-file"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>

        {parseError && (
          <DsAlert severity="error">{parseError}</DsAlert>
        )}
        {file && (
          <div className="sp-imports-fileinfo">
            <code>{file.name}</code>
            <span className="sp-imports-muted">
              {formatBytes(file.bytes)} · {file.hits.length.toLocaleString('de-DE')}{' '}
              Einträge
            </span>
          </div>
        )}

        <div className="sp-imports-row">
          <fieldset className="sp-imports-fieldset">
            <legend className="sp-imports-label">Modus</legend>
            <RadioRow
              name="mode"
              value="both"
              current={mode}
              onChange={setMode}
              label="Beides"
              hint="Vokabular säen + bestehende Artikel anreichern"
            />
            <RadioRow
              name="mode"
              value="match-fill"
              current={mode}
              onChange={setMode}
              label="Nur Match & Fill"
              hint="Nur Artikel mit GTIN-/MPN-Treffer aktualisieren"
            />
            <RadioRow
              name="mode"
              value="seed-vocabulary"
              current={mode}
              onChange={setMode}
              label="Nur Vokabular säen"
              hint="ProductLines, Modelle und Farbvokabular als Vorschläge"
            />
          </fieldset>

          <fieldset className="sp-imports-fieldset">
            <legend className="sp-imports-label">Schreibpolitik</legend>
            <RadioRow
              name="policy"
              value="fill-if-empty"
              current={policy}
              onChange={setPolicy}
              label="Fill if empty"
              hint="Nur leere Attribute überschreiben (sicher)"
            />
            <RadioRow
              name="policy"
              value="overwrite"
              current={policy}
              onChange={setPolicy}
              label="Overwrite"
              hint="Cyberport gewinnt — frühere Werte werden ersetzt"
            />
          </fieldset>
        </div>

        <div className="sp-imports-row sp-imports-actions">
          <label className="sp-imports-toggle">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            <span>Dry-Run (Transaktion wird zurückgerollt)</span>
          </label>
          <div className="sp-imports-spacer" />
          <DsButton
            variant="primary"
            disabled={!canManage || !file || importMut.isPending}
            onClick={runImport}
          >
            {importMut.isPending ? 'Importiere…' : 'Import starten'}
          </DsButton>
        </div>
      </div>

      {result && <ResultSummary result={result} />}
    </div>
  );
}

// ── Result summary ─────────────────────────────────────────────────────

function ResultSummary({ result }: { result: ImportCyberportResult }) {
  const t = result.totals;
  return (
    <div className="sp-imports-card">
      <h3 className="sp-imports-title">
        {result.dryRun ? 'Dry-Run-Vorschau' : 'Import abgeschlossen'}
      </h3>

      <div className="sp-imports-stats">
        <Stat label="Treffer" value={t.hits} />
        <Stat label="Gematcht" value={t.matched} />
        <Stat label="… via GTIN" value={t.matchedByGtin} muted />
        <Stat label="… via MPN" value={t.matchedByMpn} muted />
        <Stat label="Artikel aktualisiert" value={t.itemsUpdated} />
        <Stat label="Übersprungen" value={t.skipped} />
        <Stat
          label="ProductLines"
          value={`+${t.productLinesCreated} / ${t.productLinesExisting} bestehend`}
        />
        <Stat
          label="Modelle"
          value={`+${t.productModelsCreated} / ${t.productModelsExisting} bestehend`}
        />
        <Stat
          label="Vokabular"
          value={`+${t.vocabularyCreated} / ${t.vocabularyExisting} bestehend`}
        />
      </div>

      {Object.keys(result.skippedReasons).length > 0 && (
        <details className="sp-imports-details">
          <summary>Skip-Gründe</summary>
          <ul className="sp-imports-reasons">
            {Object.entries(result.skippedReasons)
              .sort((a, b) => b[1] - a[1])
              .map(([reason, n]) => (
                <li key={reason}>
                  <strong>{n}</strong> × {reason}
                </li>
              ))}
          </ul>
        </details>
      )}

      <ProductLinesTable rows={result.identified.productLines} />
      <ProductModelsTable rows={result.identified.productModels} />
      <VocabularyTable rows={result.identified.vocabulary} />

      <details className="sp-imports-details">
        <summary>
          Treffer-Details
          {result.resultsTruncated && (
            <span className="sp-imports-muted">
              {' '}(gekürzt auf {result.results.length.toLocaleString('de-DE')})
            </span>
          )}
        </summary>
        <div className="sp-imports-table-wrap ds-scroll">
          <table className="sp-imports-table">
            <thead>
              <tr>
                <th>Cyberport SKU</th>
                <th>Hersteller</th>
                <th>MPN</th>
                <th>GTIN</th>
                <th>Match</th>
                <th>ProductLine</th>
                <th>Modell</th>
                <th>Specs geschrieben</th>
                <th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {result.results.slice(0, 500).map((r, i) => (
                <tr key={`${r.objectID}-${i}`}>
                  <td>
                    <code>{r.objectID ?? '—'}</code>
                  </td>
                  <td>{r.manufacturer ?? '—'}</td>
                  <td>
                    <code>{r.mpn ?? '—'}</code>
                  </td>
                  <td>
                    <code>{r.gtin ?? '—'}</code>
                  </td>
                  <td>
                    {r.match ? (
                      <span className="sp-imports-tag sp-imports-tag-ok">
                        {r.match.by}: <code>{r.match.itemId}</code>
                      </span>
                    ) : (
                      <span className="sp-imports-muted">—</span>
                    )}
                  </td>
                  <td>
                    {r.identity?.productLineName ? (
                      <>
                        {r.identity.productLineName}
                        {r.identity.productLineCreated && (
                          <span className="sp-imports-new">neu</span>
                        )}
                      </>
                    ) : (
                      <span className="sp-imports-muted">—</span>
                    )}
                  </td>
                  <td>
                    {r.identity?.modelName ? (
                      <>
                        {r.identity.modelName}
                        {r.identity.modelCreated && (
                          <span className="sp-imports-new">neu</span>
                        )}
                      </>
                    ) : (
                      <span className="sp-imports-muted">—</span>
                    )}
                  </td>
                  <td>
                    {r.specsWritten.length > 0 ? (
                      r.specsWritten.join(', ')
                    ) : (
                      <span className="sp-imports-muted">—</span>
                    )}
                  </td>
                  <td>
                    {r.skipped ? (
                      <span className="sp-imports-tag sp-imports-tag-skip">
                        {r.skipped}
                      </span>
                    ) : (
                      <span className="sp-imports-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.results.length > 500 && (
            <p className="sp-imports-muted sp-imports-help">
              Es werden nur die ersten 500 Treffer angezeigt. Die Gesamtsummen
              oben enthalten alle Einträge.
            </p>
          )}
        </div>
      </details>
    </div>
  );
}

// ── Identified entity tables ──────────────────────────────────────────

function ProductLinesTable({ rows }: { rows: IdentifiedProductLine[] }) {
  if (rows.length === 0) return null;
  const created = rows.filter((r) => r.created).length;
  return (
    <details className="sp-imports-details" open>
      <summary>
        Identifizierte ProductLines{' '}
        <span className="sp-imports-muted">
          ({rows.length} gesamt, davon {created} neu)
        </span>
      </summary>
      <div className="sp-imports-table-wrap ds-scroll">
        <table className="sp-imports-table">
          <thead>
            <tr>
              <th>Hersteller</th>
              <th>Kategorie</th>
              <th>ProductLine</th>
              <th>Status</th>
              <th>Treffer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`line-${r.id}`}>
                <td>{r.manufacturerName}</td>
                <td>
                  <span className="sp-imports-muted">{r.category}</span>
                </td>
                <td>
                  {r.name}
                  {r.created && <span className="sp-imports-new">neu</span>}
                </td>
                <td>
                  <span className="sp-imports-muted">
                    {r.created ? 'erstellt' : 'bestehend'}
                  </span>
                </td>
                <td className="sp-imports-num">
                  {r.hits.toLocaleString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function ProductModelsTable({ rows }: { rows: IdentifiedProductModel[] }) {
  if (rows.length === 0) return null;
  const created = rows.filter((r) => r.created).length;
  return (
    <details className="sp-imports-details">
      <summary>
        Identifizierte Modelle{' '}
        <span className="sp-imports-muted">
          ({rows.length} gesamt, davon {created} neu)
        </span>
      </summary>
      <div className="sp-imports-table-wrap ds-scroll">
        <table className="sp-imports-table">
          <thead>
            <tr>
              <th>Hersteller</th>
              <th>ProductLine</th>
              <th>Modell</th>
              <th>Status</th>
              <th>Treffer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`model-${r.id}`}>
                <td>{r.manufacturerName}</td>
                <td>{r.productLineName}</td>
                <td>
                  {r.name}
                  {r.created && <span className="sp-imports-new">neu</span>}
                </td>
                <td>
                  <span className="sp-imports-muted">
                    {r.created ? 'erstellt' : 'bestehend'}
                  </span>
                </td>
                <td className="sp-imports-num">
                  {r.hits.toLocaleString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function VocabularyTable({ rows }: { rows: IdentifiedVocabulary[] }) {
  if (rows.length === 0) return null;
  const created = rows.filter((r) => r.created).length;
  return (
    <details className="sp-imports-details">
      <summary>
        Identifiziertes Vokabular{' '}
        <span className="sp-imports-muted">
          ({rows.length} gesamt, davon {created} neu)
        </span>
      </summary>
      <div className="sp-imports-table-wrap ds-scroll">
        <table className="sp-imports-table">
          <thead>
            <tr>
              <th>Hersteller</th>
              <th>Attribut</th>
              <th>Wert</th>
              <th>Status</th>
              <th>Treffer</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`vocab-${r.id}`}>
                <td>{r.manufacturerName}</td>
                <td>
                  <span className="sp-imports-muted">{r.attribute}</span>
                </td>
                <td>
                  {r.value}
                  {r.created && <span className="sp-imports-new">neu</span>}
                </td>
                <td>
                  <span className="sp-imports-muted">
                    {r.created ? 'erstellt' : 'bestehend'}
                  </span>
                </td>
                <td className="sp-imports-num">
                  {r.hits.toLocaleString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// ── tiny helpers ──────────────────────────────────────────────────────

function RadioRow<T extends string>({
  name,
  value,
  current,
  onChange,
  label,
  hint,
}: {
  name: string;
  value: T;
  current: T;
  onChange: (v: T) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="sp-imports-radio">
      <input
        type="radio"
        name={name}
        checked={current === value}
        onChange={() => onChange(value)}
      />
      <span className="sp-imports-radio-label">
        <strong>{label}</strong>
        {hint && <span className="sp-imports-muted">{hint}</span>}
      </span>
    </label>
  );
}

function Stat({
  label,
  value,
  muted,
}: {
  label: string;
  value: number | string;
  muted?: boolean;
}) {
  return (
    <div className={`sp-imports-stat${muted ? ' sp-imports-stat-muted' : ''}`}>
      <span className="sp-imports-stat-label">{label}</span>
      <span className="sp-imports-stat-value">
        {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
      </span>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
